import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { unzipSync, zipSync } from "fflate";
import "./Requestor.css";
import { McFileUpload } from "@maersk-global/mds-react-wrapper/components-core/mc-file-upload";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McDialog } from "@maersk-global/mds-react-wrapper/components-core/mc-dialog";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMsal, useAccount } from '@azure/msal-react';
import { jwtDecode } from "jwt-decode";
import { AL } from "../../al";
import { useUser } from "../../contexts/UserContext";

function RequestCharge() {
    const [excelFile, setExcelFile] = useState(null);
    const [zipFiles, setZipFiles] = useState(null);
    const [foNumbers, setFoNumbers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [processingExcel, setProcessingExcel] = useState(false);
    const sessionIdRef = useRef(`session_${Date.now()}`);
    const foCacheRef = useRef({});
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastAppearance, setToastAppearance] = useState("info");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [poLocations, setPoLocations] = useState({});
    const zipFilesRef = useRef(null);
    const { instance, accounts } = useMsal();
    const { userInfo, roles, loading: userLoading, token } = useUser();
    // Add task status state
    const [taskStatusData, setTaskStatusData] = useState([]);
    const [loadingTaskStatus, setLoadingTaskStatus] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewSessionId, setPreviewSessionId] = useState("");

    const [uniqueID, setUniqueID] = useState("");
    const account = userInfo;
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [groupByFO, setGroupByFO] = useState(false);

    // Validation state
    const [validationResults, setValidationResults] = useState(null);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validatingExcel, setValidatingExcel] = useState(false);

    // Rejected FO details state
    const [rejectedFODetails, setRejectedFODetails] = useState(null);
    const [showRejectedFOModal, setShowRejectedFOModal] = useState(false);

    // Helper function to parse rejected FO numbers from remarks
    const parseRejectedFOs = (remarks) => {
        if (!remarks) return [];
        // Format: "FO numbers not found in master data: FO1, FO2, FO3" or "FO numbers not found in master data: 1"
        const match = remarks.match(/FO numbers not found in master data:\s*(.+)$/i);
        if (match && match[1]) {
            return match[1].split(',').map(fo => fo.trim()).filter(fo => fo);
        }
        return [];
    };

    // ========== VALIDATION COLUMN CONFIGURATION ==========
    // Column names as they appear in the Excel template (snake_case format)
    const VALIDATION_CONFIG = {
        // Key column for determining validation rules
        MATERIAL_NAME_COLUMN: 'material_name',
        MRS_NON_MRS_COLUMN: 'mrs_non_mrs',
        
        // Scenario 1: Columns that become mandatory for Detention/Demurrage
        DETENTION_DEMURRAGE_STORAGE_MANDATORY: [
            'dd_storage_start',
            'dd_storage_end',
            'total_days'
        ],
        
        // Scenario 2: Column that becomes mandatory for Waiting
        WAITING_MANDATORY: [
            'total_hours_waiting'
        ],
        
        // Columns that are NEVER mandatory (excluded from Scenario 3)
        NON_MANDATORY_COLUMNS: [
            'dd_storage_start',
            'dd_storage_end',
            'total_days',
            'total_hours_waiting',
            'reason_for_rejection_return',
            'reason_for_rejection',
            'remarks',
            'lane_id',
            'material_name_in_mrs',
            'text'
        ],
        
        // Scenario 4: Column for customer return
        REJECTION_REASON_COLUMN: 'reason_for_rejection_return',
        
        // Base mandatory columns (always required regardless of Material Name)
        // Note: lane_id and material_name_in_mrs are conditionally mandatory based on mrs_non_mrs
        BASE_MANDATORY_COLUMNS: [
            'fo',
            'mrs_non_mrs',
            'currency',
            'origin_charge_destination_charge',
            'material_name',
            'maersk_code',
            'uom',
            'qty',
            'unit_price',
            'amount',
            'plant',
        ],
        
        // MRS/Non-MRS based conditional mandatory rules
        // lane_id is mandatory when mrs_non_mrs contains "(MRS)" but NOT "Charge Deviation"
        // material_name_in_mrs is mandatory for specific MRS charge types
        MATERIAL_IN_MRS_MANDATORY_PATTERNS: [
            'oce additional charge (mrs)',
            'air additional charge (mrs)',
            'oog additional charge (mrs)'
        ]
    };

    const columnHeaderMap = {
        fo: "FO Number",
        text: "Charge Description",
        mrs_non_mrs: "MRS/Non-MRS",
        lane_id: "Lane ID",
        currency: "Currency",
        origin_charge_destination_charge: "Origin/Destination",
        maersk_code: "Maersk Code",
        material_name: "Material Name",
        material_name_in_mrs: "Material in MRS",
        vestas_code: "Vestas Code",
        vestas_name: "Vestas Name",
        uom: "UOM",
        total_days: "Total Days",
        qty: "Quantity",
        unit_price: "Unit Price",
        amount: "Amount",
        plant: "Plant",
        invoice_team: "Invoice Team",
        etd: "ETD",
        original_port_of_loading_city_name: "Port of Loading",
        port_of_discharge_city_name: "Port of Discharge",
        place_of_delivery_city_name: "Place of Delivery",
        place_of_receipt_city_name: "Place of Receipt",
        d_d_storage_start_date: "D&D Storage Start Date",
        d_d_storage_end_date: "D&D Storage End Date",
        total_hours_waiting_time_for_waiting_charges: "Total Hours Waiting",
        eta_last_vessel_arrival: "ETA Last Vessel Arrival",
        original_port_of_loading_city_code: "Original Port of Loading Code",
        port_of_discharge_city_code: "Port of Discharge Code",
        place_of_delivery_city_code: "Place of Delivery Code",
        place_of_receipt_city_code: "Place of Receipt Code",
        bill_of_lading_no: "CBL Reference",
        equipment_number: "Container Number",
        equipment_type: "Container Type",
        equipment_size: "Container Size",
        actual_receipt_date_ard: "Actual Receipt Date (ARD)",
        delivery_actual: "Delivery Actual Date",
        ship_from_name: "Ship From Name",
        ship_to_name: "Ship To Name",
        ship_to_street: "Ship To Street",
        ship_to_city: "Ship To City",
        ship_from_country: "Ship From Country Code",
        ship_from_country_name: "Ship From Country Name",
        freight_payer_country: "Freight Payer Country Code",
        freight_payer_country_name: "Freight Payer Country Name",
        destination_region: "Destination Region",
        prod: "Product",
        incoterms: "Incoterms",
        revised_approver: "Revised Approver",
        po_creator: "PO Creator",
        invoice_creator: "Invoice Creator",
        id: "ID"
    };

    // Task status table columns - remove upload ZIP actions column
    const taskStatusColumns = [
        { id: "file_name", label: "File Name", align: "center" },
        { id: "created_at", label: "Created At", align: "center" },
        { id: "status", label: "Status", align: "center" },
        { id: "remarks", label: "Remarks", align: "center" },
        { id: "preview", label: "Preview", align: "center" }
    ];

    const handleLogout = () => {
        AL.logoutRedirect({
            post_logout_uri: window.location.origin + "/"
        });
    };
    useEffect(() => {
        console.warn("=== TOKEN DEBUG START ===");
        
        // Debug: show what key we're looking for
        console.warn("ENV VAR key:", process.env.REACT_APP_CIAM_ACCESS_KEY);
        console.warn("All localStorage keys:", Object.keys(localStorage));
        
        // Try the env var first, fallback to literal "access_token"
        let token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        console.warn("Token from env var key:", token ? "FOUND" : "NOT FOUND");
        
        if (!token) {
            token = localStorage.getItem("access_token");
            console.warn("Token from 'access_token' key:", token ? "FOUND" : "NOT FOUND");
        }
        
        if (token) {
            try {
                const decoded = jwtDecode(token);
                console.warn("Decoded JWT keys:", Object.keys(decoded));
                console.warn("uniqueId value:", decoded.uniqueId);
                setUniqueID(decoded.uniqueId);
            } catch (err) {
                console.error("JWT decode error:", err);
            }
        } else {
            console.warn("No token found in localStorage!");
        }
        
        console.warn("=== TOKEN DEBUG END ===");
    }, []);

    const showToast = (message, appearance = "info") => {
        setToastMessage(message);
        setToastAppearance(appearance);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 4000);
    };

    // ========== VALIDATION HELPER FUNCTIONS ==========
    
    // Check if a cell value is empty/blank
    const isBlank = (value) => {
        if (value === null || value === undefined) return true;
        if (typeof value === 'string' && value.trim() === '') return true;
        return false;
    };

    // Normalize column name for comparison (lowercase, trim whitespace)
    const normalizeColumnName = (name) => {
        if (!name) return '';
        return String(name).toLowerCase().trim();
    };

    // Find column index by name (case-insensitive)
    const findColumnIndex = (headers, columnName) => {
        const normalizedTarget = normalizeColumnName(columnName);
        return headers.findIndex(header => normalizeColumnName(header) === normalizedTarget);
    };

    // Check if column name matches any in a list (case-insensitive)
    const columnNameMatches = (columnName, columnList) => {
        const normalizedName = normalizeColumnName(columnName);
        return columnList.some(col => normalizeColumnName(col) === normalizedName);
    };

    // Check if lane_id should be mandatory based on mrs_non_mrs value
    const isLaneIdMandatory = (mrsNonMrsValue) => {
        const valueLower = (mrsNonMrsValue || '').toLowerCase();
        // lane_id is mandatory if mrs_non_mrs contains "(MRS)" but NOT "charge deviation"
        const containsMRS = valueLower.includes('(mrs)');
        const containsChargeDeviation = valueLower.includes('charge deviation');
        return containsMRS && !containsChargeDeviation;
    };

    // Check if material_name_in_mrs should be mandatory based on mrs_non_mrs value
    const isMaterialInMrsMandatory = (mrsNonMrsValue) => {
        const valueLower = (mrsNonMrsValue || '').toLowerCase();
        // material_name_in_mrs is mandatory for specific patterns
        return VALIDATION_CONFIG.MATERIAL_IN_MRS_MANDATORY_PATTERNS.some(
            pattern => valueLower.includes(pattern)
        );
    };

    // Get mandatory columns based on Material Name and MRS/Non-MRS values
    const getMandatoryColumnsForRow = (materialName, mrsNonMrsValue, allColumnHeaders, isCustomerReturn = false) => {
        const mandatory = [...VALIDATION_CONFIG.BASE_MANDATORY_COLUMNS];
        const conditionalMandatory = []; // Track conditional mandatory fields with reasons
        const materialNameLower = (materialName || '').toLowerCase();

        // Scenario 1: Material Name contains "Detention" or "Demurrage" or "Storage"
        if (materialNameLower.includes('detention') || materialNameLower.includes('demurrage') || materialNameLower.includes('storage')) {
            VALIDATION_CONFIG.DETENTION_DEMURRAGE_STORAGE_MANDATORY.forEach(col => {
                mandatory.push(col);
                conditionalMandatory.push({
                    column: col,
                    reason: `Required when Material Name is "${materialName}"`
                });
            });
        }
        // Scenario 2: Material Name contains "Waiting"
        else if (materialNameLower.includes('waiting')) {
            VALIDATION_CONFIG.WAITING_MANDATORY.forEach(col => {
                mandatory.push(col);
                conditionalMandatory.push({
                    column: col,
                    reason: `Required when Material Name is "${materialName}"`
                });
            });
        }
        // Scenario 3: All other Material Names - all columns are mandatory except NON_MANDATORY_COLUMNS
        else {
            allColumnHeaders.forEach(header => {
                if (!columnNameMatches(header, VALIDATION_CONFIG.NON_MANDATORY_COLUMNS) && 
                    !columnNameMatches(header, mandatory)) {
                    mandatory.push(header);
                }
            });
        }

        // Scenario 4: Customer returning with comments - Reason column becomes mandatory
        if (isCustomerReturn) {
            if (!columnNameMatches(VALIDATION_CONFIG.REJECTION_REASON_COLUMN, mandatory)) {
                mandatory.push(VALIDATION_CONFIG.REJECTION_REASON_COLUMN);
            }
        }

        // Conditional: lane_id based on mrs_non_mrs
        if (isLaneIdMandatory(mrsNonMrsValue)) {
            if (!columnNameMatches('lane_id', mandatory)) {
                mandatory.push('lane_id');
                conditionalMandatory.push({
                    column: 'lane_id',
                    reason: `Required when MRS/Non-MRS is "${mrsNonMrsValue}"`
                });
            }
        }

        // Conditional: material_name_in_mrs based on mrs_non_mrs
        if (isMaterialInMrsMandatory(mrsNonMrsValue)) {
            if (!columnNameMatches('material_name_in_mrs', mandatory)) {
                mandatory.push('material_name_in_mrs');
                conditionalMandatory.push({
                    column: 'material_name_in_mrs',
                    reason: `Required when MRS/Non-MRS is "${mrsNonMrsValue}"`
                });
            }
        }

        return {
            columns: [...new Set(mandatory)], // Remove duplicates
            conditionalReasons: conditionalMandatory
        };
    };

    // Main validation function
    const validateExcelData = (workbook) => {
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to array of arrays (including header row)
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        if (data.length < 2) {
            return {
                isValid: false,
                totalRecords: 0,
                successfulRecords: 0,
                failedRecords: 0,
                errors: [{ row: 0, message: 'Excel file is empty or has no data rows' }],
                headers: []
            };
        }

        // Get header row (first row)
        const headers = data[0].map(h => String(h || '').trim());
        const dataRows = data.slice(1);
        
        // Find Material Name column index
        const materialNameColIndex = findColumnIndex(headers, VALIDATION_CONFIG.MATERIAL_NAME_COLUMN);
        
        if (materialNameColIndex === -1) {
            return {
                isValid: false,
                totalRecords: 0,
                successfulRecords: 0,
                failedRecords: 0,
                errors: [{ 
                    row: 0, 
                    message: `Required column "${VALIDATION_CONFIG.MATERIAL_NAME_COLUMN}" not found in Excel file` 
                }],
                headers
            };
        }

        const errors = [];
        let successfulRecords = 0;
        let failedRecords = 0;

        // Find MRS/Non-MRS column index
        const mrsNonMrsColIndex = findColumnIndex(headers, VALIDATION_CONFIG.MRS_NON_MRS_COLUMN);

        dataRows.forEach((row, index) => {
            const rowNumber = index + 2; // Excel row number (1-indexed + header row)
            const rowErrors = [];
            const rowErrorDetails = []; // Store detailed error reasons
            
            // Skip completely empty rows
            if (row.every(cell => isBlank(cell))) {
                return;
            }

            // Get Material Name from this row
            const materialName = row[materialNameColIndex] || '';
            
            // Get MRS/Non-MRS value from this row
            const mrsNonMrsValue = mrsNonMrsColIndex !== -1 ? (row[mrsNonMrsColIndex] || '') : '';
            
            // Determine if this is a customer return scenario
            // (You can customize this logic based on your business rules)
            const isCustomerReturn = false;
            
            // Get mandatory columns for this row based on Material Name and MRS/Non-MRS
            const { columns: mandatoryColumns, conditionalReasons } = getMandatoryColumnsForRow(
                materialName, 
                mrsNonMrsValue, 
                headers, 
                isCustomerReturn
            );
            
            // Build a map of conditional reasons for quick lookup
            const reasonMap = {};
            conditionalReasons.forEach(cr => {
                reasonMap[normalizeColumnName(cr.column)] = cr.reason;
            });
            
            // Validate each mandatory column
            mandatoryColumns.forEach(columnName => {
                const colIndex = findColumnIndex(headers, columnName);
                if (colIndex !== -1) {
                    const cellValue = row[colIndex];
                    if (isBlank(cellValue)) {
                        const normalizedColName = normalizeColumnName(columnName);
                        const reason = reasonMap[normalizedColName];
                        
                        if (reason) {
                            // Conditional mandatory field - add with reason
                            rowErrorDetails.push({
                                column: columnName,
                                reason: reason
                            });
                        } else {
                            // Regular mandatory field
                            rowErrorDetails.push({
                                column: columnName,
                                reason: 'Required field'
                            });
                        }
                        rowErrors.push(columnName);
                    }
                }
            });

            if (rowErrors.length > 0) {
                failedRecords++;
                errors.push({
                    row: rowNumber,
                    materialName: materialName || '(empty)',
                    mrsNonMrs: mrsNonMrsValue || '(empty)',
                    missingColumns: rowErrors,
                    errorDetails: rowErrorDetails,
                    message: `Row ${rowNumber}: Missing mandatory fields - ${rowErrors.join(', ')}`
                });
            } else {
                successfulRecords++;
            }
        });

        const totalRecords = successfulRecords + failedRecords;

        return {
            isValid: errors.length === 0,
            totalRecords,
            successfulRecords,
            failedRecords,
            errors,
            headers
        };
    };

    const excelFileRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e?.detail?.[0];
        if (file) {
            excelFileRef.current = file;
            setExcelFile(file);
            setShowPreview(false);
            setFoNumbers([]);
            // Clear previous validation results when new file is selected
            setValidationResults(null);
            setShowValidationModal(false);
        }
    };
    const handleExcelUpload = async () => {
        const fileToUpload = excelFileRef.current;
        if (!fileToUpload) {
            alert("Please select an Excel file before uploading");
            return;
        }

        // Reset validation state before starting
        setShowValidationModal(false);
        setValidationResults(null);
        
        // First, validate the Excel file
        setValidatingExcel(true);
        showToast("Validating Excel file...", "info");

        try {
            const validationResult = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const results = validateExcelData(workbook);
                        resolve(results);
                    } catch (parseError) {
                        reject(parseError);
                    }
                };

                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsArrayBuffer(fileToUpload);
            });

            setValidatingExcel(false);

            // If validation failed, show modal and banner, then stop
            if (!validationResult.isValid) {
                setValidationResults(validationResult);
                setShowValidationModal(true);
                showToast(`Validation failed: ${validationResult.failedRecords} of ${validationResult.totalRecords} records have errors.`, "error");
                return; // Stop here - don't upload
            }

            // Validation passed - just show toast and proceed (no banner/modal)
            setValidationResults(null);
            showToast(`Validation passed! Uploading ${validationResult.totalRecords} records...`, "success");

        } catch (error) {
            console.error("Validation error:", error);
            showToast("Failed to validate Excel file. Please check the file format.", "error");
            setValidatingExcel(false);
            setValidationResults({
                isValid: false,
                totalRecords: 0,
                successfulRecords: 0,
                failedRecords: 0,
                errors: [{ row: 0, message: `Parse error: ${error.message}` }],
                headers: []
            });
            setShowValidationModal(true);
            return; // Stop here - don't upload
        }

        const formData = new FormData();
        formData.append("file", fileToUpload);
        formData.append("user_id", account?.email || "unknown"); // backend expects user_id

        setProcessingExcel(true);
        showToast("Uploading Excel, please wait...", "info");

        // Get token from context or fallback to localStorage
        const accessToken = token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);


        if (!accessToken) {
            showToast("Authentication token not found", "error");
            setProcessingExcel(false);
            return;
        }

        try {
            const uploadRes = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/upload-excel/`,
                formData,
                {
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                        "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                    },
                }
            );

            const { status, session_id } = uploadRes.data;
            sessionIdRef.current = session_id;
            
            // Show success message
            showToast("Excel uploaded successfully!", "success");
            
            // Clear validation results (no need to show modal on success)
            setValidationResults(null);
            
            // Clear the file selection
            setExcelFile(null);
            excelFileRef.current = null;
            
            // Refresh task status data to show the new upload
            if (uniqueID) {
                try {
                    console.log("API being called: ", `${process.env.REACT_APP_API_BASE_URL}/api/task-status/${uniqueID}`);
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/api/task-status/${uniqueID}`,
                        {
                            headers: {
                                "Authorization": `Bearer ${accessToken}`,
                                "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                            }
                        }
                    );
                    setTaskStatusData(response.data.records || []);
                } catch (fetchError) {
                    console.error("Error refreshing task status:", fetchError);
                }
            } else {
                console.log("No unique ID found. Cannot refresh task status data.");
            }
        } catch (err) {
            console.error(err);
            showToast("Excel upload failed: " + (err.response?.data?.message || err.message), "error");
        } finally {
            setProcessingExcel(false);
            window.location.reload();
        }
    };



    const performSubmit = async () => {
        setLoadingSubmit(true);

        try {
            // 1️⃣ Upload ZIP files if any
            if (zipFilesRef.current && zipFilesRef.current.length > 0) {
                const zipFormData = new FormData();
                zipFormData.append("session_id", sessionIdRef.current);

                // Add the actual ZIP files to FormData
                Array.from(zipFilesRef.current).forEach((file, index) => {
                    zipFormData.append("files", file); // or use `files[]` if your backend expects array notation
                });

                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/upload-zips/`, zipFormData);
                showToast(`✅ Uploaded ${zipFilesRef.current.length} ZIP file(s)`, "info");
            } else {
                const proceed = window.confirm("⚠️ No ZIP files uploaded. Continue?");
                if (!proceed) return;
            }


            // 2️⃣ Send mail
            const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/send-mail/`, {
                session_id: sessionIdRef.current,
                source: account.email,
            });

            const sent = res.data.sent || [];
            const failed = res.data.failed || {};
            const salesforce = res.data.salesforce || {};

            showToast(
                ` Sent: ${sent.join(", ")}\n`,
                "info"
            );

            // 3️⃣ Extract locations for each PO
            const formattedLocations = {};
            Object.entries(salesforce).forEach(([po, locArray]) => {
                formattedLocations[po] = locArray.flatMap((locObj) => {
                    const responses = locObj?.body?.compositeResponse || [];
                    return responses.map((resp) => resp?.httpHeaders?.Location || "N/A");
                });
            });
            setPoLocations(formattedLocations);

        } catch (err) {
            showToast("Error occurred while sending emails.", "error");
        } finally {
            setLoadingSubmit(false);
        }
    };



    const downloadTemplate = async () => {
        try {
            const accessToken = token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
            if (!accessToken) {
                showToast("Authentication token not found", "error");
                return;
            }

            showToast("Preparing template...", "info");

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/download-template`, {
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                }
            });

            if (!res.ok) {
                showToast("Failed to download template", "error");
                return;
            }

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "request_charge_template.xlsx";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showToast("Template downloaded", "success");
        } catch (err) {
            console.error("Template download failed:", err);
            showToast("Template download failed", "error");
        }
    };


    const handleGoBack = () => {
        navigate('/');
    };
    const handleDialogClick = (e) => {
        // get the attribute from the clicked button
        const action = e.target.getAttribute("dialogaction");

        setDialogOpen(false);

        if (action === "ok") {
            performSubmit();
            alert("Submission successful!");
        } else if (action === "cancel") {
            alert("Submission cancelled.");
        }
    };
    useEffect(() => {
        const fetchTaskStatus = async () => {
            if (!uniqueID) return;
            setLoadingTaskStatus(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/task-status/${uniqueID}`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                             "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                        }
                    }
                );
                setTaskStatusData(response.data.records || []);
            } catch (error) {
                if (error.response?.status !== 404) {
                    showToast("Failed to load task status", "error");
                }
            } finally {
                setLoadingTaskStatus(false);
            }
        };

        fetchTaskStatus();
    }, [uniqueID]);

    const handlePreviewForSession = async (sessionId) => {
        // Find the filename for this session from taskStatusData
        const taskData = taskStatusData.find(task => task.session_id === sessionId);
        const file_name = taskData?.file_name || 'Unknown File';

        // Navigate to the preview page with both sessionId and filename
        navigate(`/preview/${sessionId}`);
    };

    // Add function to refresh task status data
    const refreshTaskStatus = async () => {
        if (!uniqueID) return;
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/task-status/${uniqueID},`,
                 {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                             "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                        }
                    }
            );
            setTaskStatusData(response.data.records || []);
        } catch (error) {
            console.error('Error refreshing task status:', error);
        }
    };

    // Add event listener for ZIP upload events
    useEffect(() => {
        // Define global functions on the window object
        window.handleZipFileChange = function (sessionId, files) {
            if (files && files.length > 0) {
                const event = new CustomEvent('zipUploadForSession', {
                    detail: { sessionId, files }
                });
                window.dispatchEvent(event);
            }
        };

        window.handlePreviewClick = function (sessionId) {
            // Refresh task status data before handling preview to ensure we have latest data
            refreshTaskStatus().then(() => {
                const event = new CustomEvent('previewForSession', {
                    detail: { sessionId }
                });
                window.dispatchEvent(event);
            });
        };

        window.handleDetailsClick = function (sessionId, remarks, fileName) {
            const event = new CustomEvent('showRejectedDetails', {
                detail: { sessionId, remarks, fileName }
            });
            window.dispatchEvent(event);
        };

        window.handleZipUploadClick = function (sessionId, rowId) {
            const fileInput = document.getElementById(`zip-upload-${rowId}`);
            if (fileInput) {
                fileInput.click();
            } else {
                console.error('File input not found:', `zip-upload-${rowId}`);
            }
        };

        const handleZipUploadEvent = (event) => {
            const { sessionId, files } = event.detail;
            handleZipUploadForSession(sessionId, files);
        };

        const handlePreviewEvent = (event) => {
            const { sessionId } = event.detail;
            handlePreviewForSession(sessionId);
        };

        const handleRejectedDetailsEvent = (event) => {
            const { sessionId, remarks, fileName } = event.detail;
            const rejectedFOs = parseRejectedFOs(remarks);
            setRejectedFODetails({
                sessionId: sessionId,
                missingFOs: rejectedFOs,
                fileName: fileName,
                rawRemarks: remarks
            });
            setShowRejectedFOModal(true);
        };

        window.addEventListener('zipUploadForSession', handleZipUploadEvent);
        window.addEventListener('previewForSession', handlePreviewEvent);
        window.addEventListener('showRejectedDetails', handleRejectedDetailsEvent);

        return () => {
            window.removeEventListener('zipUploadForSession', handleZipUploadEvent);
            window.removeEventListener('previewForSession', handlePreviewEvent);
            window.removeEventListener('showRejectedDetails', handleRejectedDetailsEvent);
            // Clean up global functions
            delete window.handleZipFileChange;
            delete window.handlePreviewClick;
            delete window.handleDetailsClick;
            delete window.handleZipUploadClick;
        };
    }, [account?.email]);

    const handleZipUploadForSession = async (sessionId, files) => {
        if (!files || files.length === 0) {
            showToast("Please select ZIP files", "error");
            return;
        }

        try {
            const zipFormData = new FormData();
            zipFormData.append("session_id", sessionId);

            Array.from(files).forEach((file, index) => {
                zipFormData.append("files", file);
            });

            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/upload-zips/`, zipFormData);
            showToast(`✅ Uploaded ${files.length} ZIP file(s) for session`, "success");
            await refreshTaskStatus();
        } catch (error) {
            showToast("Failed to upload ZIP files", "error");
        }
    };

    return (
        <div className="mds">
            <McTopBar
                product="Maersk | Unplanned Charges"
                 productshort="Maersk"
                style={{
                    paddingLeft: "20px",
                    height: "64px",
                    paddingRight: "20px",
                    marginTop: "-5px",
                    borderBottom: "1px solid #DBDBDB",
                }}
            >

                <div slot="actions" style={{ display: "flex", alignItems: "center", color: "white" }}>
                    <McButton
                        onClick={handleGoBack}
                        label="Go to Home"
                        appearance="primary"
                        fit="small"
                        style={{ marginRight: "1rem" }}
                    />
                    <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
                </div>
            </McTopBar>


            <main className="mds-page mds-container" style={{ padding: "2rem", minHeight: "95vh" }}>
                <section style={{ marginBottom: "2rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                        <h3 className="main-heading" style={{ margin: 0 }}>Upload Input Excel</h3>
                        <McButton
                            onClick={downloadTemplate}
                            label="Download Template"
                            appearance="secondary"
                            fit="small"
                        />
                    </div>
                    <div className="uplaod-section-sub">
                        <McFileUpload label="Select Excel File" style={{ marginRight: '1rem' }} accept=".xlsx" input={handleFileChange} />

                        <McButton
                            onClick={handleExcelUpload}
                            appearance="secondary"
                            className="upload-button"
                            disabled={processingExcel || validatingExcel || !excelFile}
                        >
                            {validatingExcel ? "Validating..." : processingExcel ? "Processing Excel..." : "Upload Input Excel"}
                        </McButton>
                    </div>
                    
                    {/* Validation Summary Banner */}
                    {validationResults && (
                        <div style={{ 
                            marginTop: '1rem', 
                            padding: '0.75rem 1rem',
                            borderRadius: '6px',
                            backgroundColor: validationResults.isValid ? '#e6f4ea' : '#fce8e6',
                            border: `1px solid ${validationResults.isValid ? '#34a853' : '#ea4335'}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ 
                                fontWeight: 600, 
                                color: validationResults.isValid ? '#137333' : '#c5221f' 
                            }}>
                                {validationResults.isValid ? 'Validation Passed' : 'Validation Failed'}
                            </span>
                            <span style={{ color: '#5f6368' }}>
                                | Total: {validationResults.totalRecords} | 
                                Success: {validationResults.successfulRecords} | 
                                Failed: {validationResults.failedRecords}
                            </span>
                            <McButton 
                                onClick={() => setShowValidationModal(true)} 
                                appearance="neutral" 
                                fit="small"
                                style={{ marginLeft: 'auto' }}
                            >
                                {validationResults.isValid ? 'View Details' : 'View Errors'}
                            </McButton>
                        </div>
                    )}
                </section>

                {/* Task Status Section */}
                <section style={{ marginBottom: "2rem" }}>
                    <h3 className="main-heading">Upload History</h3>
                    {loadingTaskStatus ? (
                        <div style={{ padding: "1rem", textAlign: "center" }}>
                            <span>Loading upload history...</span>
                        </div>
                    ) : taskStatusData.length > 0 ? (
                        <div style={{
                            border: taskStatusData.length > 5 ? "1px solid #ddd" : "none",
                            borderRadius: "8px",
                            overflow: "hidden",
                            height: taskStatusData.length > 5 ? "400px" : "auto",
                            display: "flex",
                            flexDirection: "column"
                        }}>
                            <div style={{
                                flex: 1,
                                overflowY: taskStatusData.length > 5 ? "auto" : "visible",
                                minHeight: "0",
                            }}>
                                <McTable
                                    data={taskStatusData.map((item, index) => {
                                        // Format date as DD/MM/YYYY, HH:MM:SS AM/PM in user's local time
                                        // Append 'Z' to treat the timestamp as UTC, then convert to local
                                        const utcTimestamp = item.created_at.endsWith('Z') ? item.created_at : item.created_at + 'Z';
                                        const date = new Date(utcTimestamp);
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const year = date.getFullYear();
                                        let hours = date.getHours();
                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                        const seconds = String(date.getSeconds()).padStart(2, '0');
                                        const ampm = hours >= 12 ? 'PM' : 'AM';
                                        hours = hours % 12;
                                        hours = hours ? hours : 12; // 0 should be 12
                                        const formattedHours = String(hours).padStart(2, '0');
                                        const formattedDate = `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
                                        
                                        return {
                                            ...item,
                                            id: index,
                                            created_at: formattedDate,
                                            status: item.status || 'pending',
                                            remarks: item.remarks || '-'
                                        };
                                    })}
                                    
                                    columns={taskStatusColumns.map((col) => ({
                                        ...col,
                                        cellTemplate: (e) => {
                                            const val = e.rowData[col.id] || "-";
                                            let displayVal = val.toString();

                                            // Special formatting for status
                                            if (col.id === 'status') {
                                                const status = val.toLowerCase();
                                                const appearance =
                                                    status === 'completed' ? 'success' :
                                                        status === 'failed' ? 'error' :
                                                            status === 'rejected' ? 'error' :
                                                                status === 'processing' ? 'warning' : 'neutral';

                                                return e.html`
                                                    <mc-tag label="${displayVal}" appearance="${appearance}"></mc-tag>`;
                                            }

                                            // Custom display for remarks column when status is rejected
                                            if (col.id === 'remarks') {
                                                const status = e.rowData.status || '';
                                                const isRejected = status.toLowerCase() === 'rejected';
                                                
                                                if (isRejected) {
                                                    const rejectedFOs = parseRejectedFOs(val.toString());
                                                    const customMessage = rejectedFOs.length > 0
                                                        ? "Some FOs were not found in the master data. Click the Details button to know more."
                                                        : displayVal;
                                                    return e.html`
                                                        <span title="${customMessage}">
                                                            ${customMessage}
                                                        </span>`;
                                                }
                                                
                                                return e.html`
                                                    <span title="${val.toString()}">
                                                        ${displayVal}
                                                    </span>`;
                                            }

                                            // Add preview functionality for preview column
                                            if (col.id === 'preview') {
                                                const sessionId = e.rowData.session_id;
                                                const status = e.rowData.status || '';
                                                const remarks = e.rowData.remarks || '';
                                                const isCompleted = status.toLowerCase() === 'completed';
                                                const isRejected = status.toLowerCase() === 'rejected';
                                                const isSubmitted = remarks.toLowerCase().includes('submitted');
                                                const isPreviewEnabled = isCompleted && !isSubmitted;

                                                // Show "Details" button for rejected uploads
                                                if (isRejected) {
                                                    const fileName = e.rowData.file_name || '';
                                                    // Escape any quotes in remarks/fileName for the onclick handler
                                                    const escapedRemarks = remarks.replace(/'/g, "\\'");
                                                    const escapedFileName = fileName.replace(/'/g, "\\'");
                                                    return e.html`
                                                        <mc-button 
                                                            onclick="handleDetailsClick('${sessionId}', '${escapedRemarks}', '${escapedFileName}')"
                                                            style="  
                                                                color: white;
                                                                border: none;
                                                                padding: 4px 8px;
                                                                border-radius: 4px;
                                                                cursor: pointer;
                                                                font-size: 12px;
                                                                font-color: red;
                                                                min-width: 70px;
                                                            "
                                                            appearance="secondary"
                                                            variant="outlined"
                                                            width="full-width"
                                                            fit=small
                                                            title="View rejection details"
                                                        >
                                                             Details
                                                        </mc-button>`;
                                                }

                                                return e.html`
                                                    <mc-button 
                                                        onclick="${isPreviewEnabled ? `handlePreviewClick('${sessionId}')` : ''}"
                                                        style="  
                                                            color: white;
                                                            border: none;
                                                            padding: 4px 8px;
                                                            border-radius: 4px;
                                                            cursor: ${isPreviewEnabled ? 'pointer' : 'not-allowed'};
                                                            font-size: 12px;
                                                            opacity: ${isPreviewEnabled ? '1' : '0.6'};
                                                            min-width: 70px;
                                                        "
                                                        fit=small
                                                        appearance="secondary"
                                                        width="full-width"
                                                        title="${isPreviewEnabled ? 'Preview data for this session' :
                                                        isSubmitted ? 'Preview disabled - already submitted' :
                                                            'Preview available only when status is completed'}"
                                                        ${isPreviewEnabled ? '' : 'disabled'}
                                                    >
                                                         Preview
                                                    </mc-button>`;
                                            }

                                            return e.html`
                                                <span title="${val.toString()}">
                                                    ${displayVal}
                                                </span>`;
                                        },
                                    }))}
                                    datakey="id"
                                    height="auto"
                                    sortdisabled
                                    recordsperpage={taskStatusData.length || 10}
                                    striped={true}
                                    stickyHeader={true}
                                    style={{
                                        border: "none",
                                        minHeight: "100%"
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            padding: "2rem",
                            textAlign: "center",
                            color: "#666",
                            border: "1px dashed #ddd",
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9"
                        }}>
                            <span>No upload history found</span>
                        </div>
                    )}
                </section>

                {showPreview && (
                    <section style={{ marginBottom: "2rem" }} className="flex justify-start gap-4">
                        <McButton appearance="primary" onClick={() => setShowModal(true)}>
                            Preview FO Data
                        </McButton>
                    </section>
                )}

                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white w-[90vw] h-[90vh] rounded-xl shadow-lg overflow-auto p-6 relative flex flex-col">
                            <div className="flex items-center mb-4" style={{ justifyContent: "space-between" }}>
                                <h3 className="text-xl font-semibold" style={{ marginBottom: "1rem" }}>
                                    Preview FO Data
                                </h3>
                                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <input
                                            type="checkbox"
                                            checked={groupByFO}
                                            onChange={(e) => {
                                                setGroupByFO(e.target.checked);
                                                setPage(1); // Reset to first page when toggling group
                                            }}
                                        />
                                        Group by FO
                                    </label>
                                    <McButton onClick={() => setShowModal(false)} label="Close" appearance="secondary" fit="small" />
                                </div>
                            </div>
                            {Object.keys(foCacheRef.current).length === 0 ? (
                                <p className="text-center text-gray-500">No data available</p>
                            ) : (
                                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                                    {(() => {
                                        if (groupByFO) {
                                            // Group data by FO
                                            const groupedData = {};
                                            Object.entries(foCacheRef.current).forEach(([foKey, foData]) => {
                                                if (!groupedData[foKey]) {
                                                    groupedData[foKey] = [];
                                                }
                                                groupedData[foKey] = foData;
                                            });

                                            const foKeys = Object.keys(groupedData);
                                            const totalFOPages = Math.ceil(foKeys.length / pageSize);
                                            const startIdx = (page - 1) * pageSize;
                                            const endIdx = startIdx + pageSize;
                                            const paginatedFOs = foKeys.slice(startIdx, endIdx);

                                            return (
                                                <div style={{ flex: 1, overflowY: "auto" }}>
                                                    {paginatedFOs.map((foKey) => (
                                                        <div key={foKey} style={{ marginBottom: "2rem", border: "1px solid #ddd", borderRadius: "8px", padding: "1rem" }}>
                                                            <h4 style={{
                                                                backgroundColor: "#f0f4f8",
                                                                padding: "0.5rem",
                                                                margin: "-1rem -1rem 1rem -1rem",
                                                                borderRadius: "8px 8px 0 0",
                                                                fontWeight: "600",
                                                                fontSize: "1.1rem"
                                                            }}>
                                                                FO: {foKey} ({groupedData[foKey].length} records)
                                                            </h4>
                                                            <McTable
                                                                data={groupedData[foKey]}
                                                                columns={Object.keys(groupedData[foKey][0] || {}).map((key) => ({
                                                                    id: key,
                                                                    label: columnHeaderMap[key] || key,
                                                                    noWrap: true,
                                                                    cellStyle: key === "fo" ? { backgroundColor: "#f0f4f8", fontWeight: 600 } : {},
                                                                }))}
                                                                datakey="id"
                                                                height="auto"
                                                                recordsperpage={50} // Show more records per FO group
                                                                selected={[]}
                                                                hidecolumns={[]}
                                                                striped={true}
                                                                stickyHeader={false}
                                                                style={{
                                                                    border: "none",
                                                                    borderRadius: "4px",
                                                                }}
                                                            />
                                                        </div>
                                                    ))}

                                                    {/* Pagination for FO groups */}
                                                    <div style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        gap: "1rem",
                                                        padding: "1rem",
                                                        borderTop: "1px solid #eee",
                                                        backgroundColor: "#f9f9f9"
                                                    }}>
                                                        <McButton
                                                            onClick={() => {
    
                                                                setPage(prevPage => {
                                                                    const newPage = Math.max(1, prevPage - 1);
                                                                    return newPage;
                                                                });
                                                            }}
                                                            disabled={page === 1}
                                                            appearance="secondary"
                                                            fit="small"
                                                        >
                                                            Previous
                                                        </McButton>
                                                        <span>
                                                            Page {page} of {totalFOPages}
                                                            ({foKeys.length} FO groups total)
                                                        </span>
                                                        <McButton
                                                            onClick={() => {
                                                                setPage(prevPage => {
                                                                    const newPage = prevPage + 1;
                                                                    if (newPage <= totalFOPages) {
                                                                        return newPage;
                                                                    } else {
                                                                        return prevPage;
                                                                    }
                                                                });
                                                            }}
                                                            disabled={page >= totalFOPages}
                                                            appearance="secondary"
                                                            fit="small"
                                                        >
                                                            Next
                                                        </McButton>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            // Original flat view with pagination
                                            const allData = Object.values(foCacheRef.current).flat();
                                            const totalPages = Math.ceil(allData.length / pageSize);
                                            const startIdx = (page - 1) * pageSize;
                                            const endIdx = startIdx + pageSize;
                                            const paginatedData = allData.slice(startIdx, endIdx);

                                            return (
                                                <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                                    <McTable
                                                        data={paginatedData}
                                                        columns={Object.keys(allData[0] || {}).map((key) => ({
                                                            id: key,
                                                            label: columnHeaderMap[key] || key,
                                                            noWrap: true,
                                                            cellStyle:
                                                                key === "fo"
                                                                    ? { backgroundColor: "#f0f4f8", fontWeight: 600 }
                                                                    : {},
                                                        }))}
                                                        datakey="id"
                                                        height="auto"
                                                        recordsperpage={pageSize}
                                                        selected={[]}
                                                        hidecolumns={[]}
                                                        striped={true}
                                                        stickyHeader={true}
                                                        style={{
                                                            flex: 1,
                                                            border: "1px solid #eee",
                                                            borderRadius: "8px",
                                                        }}
                                                    />

                                                    {/* Custom pagination for flat view */}
                                                    <div style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        gap: "1rem",
                                                        padding: "1rem",
                                                        borderTop: "1px solid #eee",
                                                        backgroundColor: "#f9f9f9",
                                                        marginTop: "1rem"
                                                    }}>
                                                        <McButton
                                                            onClick={() => {
                                                                setPage(prevPage => {
                                                                    const newPage = Math.max(1, prevPage - 1);
                                                                   
                                                                    return newPage;
                                                                });
                                                            }}
                                                            disabled={page === 1}
                                                            appearance="secondary"
                                                            fit="small"
                                                        >
                                                            Previous
                                                        </McButton>

                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                            <span>Page {page} of {totalPages}</span>
                                                            <select
                                                                value={pageSize}
                                                                onChange={(e) => {
                                                                    setPageSize(Number(e.target.value));
                                                                    setPage(1); // Reset to first page when changing page size
                                                                }}
                                                                style={{
                                                                    padding: "4px 8px",
                                                                    borderRadius: "4px",
                                                                    border: "1px solid #ccc"
                                                                }}
                                                            >
                                                                <option value={10}>10 per page</option>
                                                                <option value={20}>20 per page</option>
                                                                <option value={30}>30 per page</option>
                                                                <option value={40}>40 per page</option>
                                                            </select>
                                                        </div>

                                                        <span>({allData.length} records total)</span>

                                                        <McButton
                                                            onClick={() => {
                                                                setPage(prevPage => {
                                                                    const newPage = prevPage + 1;
                                                                    if (newPage <= totalPages) {                                                             
                                                                        return newPage;
                                                                    } else {       
                                                                        return prevPage;
                                                                    }
                                                                });
                                                            }}
                                                            disabled={page >= totalPages}
                                                            appearance="secondary"
                                                            fit="small"
                                                        >
                                                            Next
                                                        </McButton>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                )}




                {/* <section style={{ marginBottom: "2rem" }}>
                    <h3 className="main-heading">2. Upload ZIP Folders</h3>
                    <div className="flex gap-4 items-center">
                        <McFileUpload
                            accept=".zip"
                            multiple
                            input={(e) => {
                                zipFilesRef.current = e?.detail || null;
                                setZipFiles(e?.detail || null); // keep state for render if needed
                            }}
                        />

                    </div>
                </section>

                <section style={{ marginBottom: "2rem" }} className="flex gap-4">
                    <McButton
                        onClick={performSubmit}
                        appearance="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit ? "Submitting..." : "Submit"}
                    </McButton>

                </section>
                {Object.keys(poLocations).length > 0 && (
                    <section className="po-locations-section">
                        <p className="section-subtitle">
                            The following Purchase Orders have been successfully submitted. Click "Edit/View" to see or modify the charge details.
                        </p>

                        <div className="po-cards-container">
                            {Object.entries(poLocations).map(([po, paths]) => {
                                // Only keep paths that belong to charge referenceIds
                                const uniquePaths = Array.from(new Set(paths)).filter((path) =>
                                    path.includes("Charge__c")
                                );

                                if (uniquePaths.length === 0) return null; // skip if no charge paths

                                return (
                                    <div key={po} className="po-card">
                                        <div className="po-card-header">
                                            <h4 className="po-number">FO: {po}</h4>
                                            <span className="po-badge">Success</span>
                                        </div>
                                        <ul className="charge-list">
                                            {uniquePaths.map((path, idx) => {
                                                const salesforceId = path.split("/").pop();
                                                const url = `https://maersk--preprod.sandbox.my.salesforce.com/a7W9V000000L5r7UAC/${salesforceId}`;

                                                return (
                                                    <li key={idx} className="charge-item">
                                                        <span className="charge-id">Charge ID {idx + 1}</span>
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="charge-link"
                                                        >
                                                            View
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )} */}





            </main>

            {/* Validation Results Modal */}
            {showValidationModal && validationResults && (
                <div style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 9999 
                }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        width: '90vw', 
                        maxWidth: '1000px',
                        maxHeight: '85vh', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', 
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Modal Header */}
                        <div style={{ 
                            padding: '1.5rem', 
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: validationResults.isValid ? '#e6f4ea' : '#fce8e6'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                                    {validationResults.isValid ? 'Validation Successful' : 'Validation Failed - Missing Mandatory Fields'}
                                </h2>
                                {/* <McButton 
                                    onClick={() => setShowValidationModal(false)} 
                                    appearance="neutral" 
                                    fit="small"
                                >
                                    Close
                                </McButton> */}
                            </div>
                        </div>

                        {/* Summary Section */}
                        <div style={{ 
                            padding: '1rem 1.5rem', 
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex',
                            gap: '2rem',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ textAlign: 'center', minWidth: '100px' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1a73e8' }}>
                                    {validationResults.totalRecords}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#5f6368' }}>Total Records</div>
                            </div>
                            <div style={{ textAlign: 'center', minWidth: '100px' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#34a853' }}>
                                    {validationResults.successfulRecords}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#5f6368' }}>Successful Records</div>
                            </div>
                            <div style={{ textAlign: 'center', minWidth: '100px' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#ea4335' }}>
                                    {validationResults.failedRecords}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#5f6368' }}>Failed Records</div>
                            </div>
                        </div>

                        {/* Validation Rules Info */}
                        {/* <div style={{ 
                            padding: '0.75rem 1.5rem', 
                            backgroundColor: '#e8f0fe',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.8rem',
                            color: '#1967d2'
                        }}>
                            <strong>Validation Rules:</strong> 
                            <span style={{ marginLeft: '0.5rem' }}>
                                Detention/Demurrage → Start Date, End Date, Total Days required | 
                                Storage → Total Hours required | 
                                Other Materials → All fields except date/time columns required
                            </span>
                        </div> */}

                        {/* Error Details Section */}
                        <div style={{ 
                            flex: 1, 
                            overflow: 'auto', 
                            padding: '1.5rem',
                            minHeight: '200px'
                        }}>
                            {validationResults.errors.length > 0 ? (
                                <>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#c5221f' }}>
                                        Validation Error Details ({validationResults.errors.length} records with issues)
                                    </h3>
                                    <div style={{ 
                                        border: '1px solid #e0e0e0', 
                                        borderRadius: '8px',
                                        overflow: 'hidden'
                                    }}>
                                        <table style={{ 
                                            width: '100%', 
                                            borderCollapse: 'collapse',
                                            fontSize: '0.875rem'
                                        }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e0e0e0', width: '60px', fontWeight: 600 }}>Row</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e0e0e0', width: '150px', fontWeight: 600 }}>Material Name</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e0e0e0', width: '180px', fontWeight: 600 }}>MRS/Non-MRS</th>
                                                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Missing Mandatory Fields</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {validationResults.errors.slice(0, 100).map((error, idx) => (
                                                    <tr key={idx} style={{ 
                                                        borderBottom: '1px solid #e0e0e0',
                                                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                                                    }}>
                                                        <td style={{ padding: '0.75rem', fontWeight: 600, color: '#c5221f' }}>{error.row}</td>
                                                        <td style={{ padding: '0.75rem', color: '#5f6368', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={error.materialName}>
                                                            {error.materialName}
                                                        </td>
                                                        <td style={{ padding: '0.75rem', color: '#5f6368', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={error.mrsNonMrs}>
                                                            {error.mrsNonMrs || '(empty)'}
                                                        </td>
                                                        <td style={{ padding: '0.75rem' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                                                {error.errorDetails && error.errorDetails.length > 0 ? (
                                                                    error.errorDetails.map((detail, colIdx) => (
                                                                        <div key={colIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                            <span style={{
                                                                                backgroundColor: '#fce8e6',
                                                                                color: '#c5221f',
                                                                                padding: '0.2rem 0.5rem',
                                                                                borderRadius: '4px',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 500
                                                                            }}>
                                                                                {detail.column}
                                                                            </span>
                                                                            <span style={{
                                                                                color: '#666',
                                                                                fontSize: '0.7rem',
                                                                                fontStyle: 'italic'
                                                                            }}>
                                                                                ({detail.reason})
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    // Fallback for errors without details
                                                                    error.missingColumns.map((col, colIdx) => (
                                                                        <span key={colIdx} style={{
                                                                            backgroundColor: '#fce8e6',
                                                                            color: '#c5221f',
                                                                            padding: '0.2rem 0.5rem',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 500
                                                                        }}>
                                                                            {col}
                                                                        </span>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {validationResults.errors.length > 100 && (
                                            <div style={{ 
                                                padding: '0.75rem', 
                                                textAlign: 'center', 
                                                backgroundColor: '#fff3cd',
                                                color: '#856404',
                                                fontWeight: 500
                                            }}>
                                                Showing first 100 errors. Total errors: {validationResults.errors.length}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '3rem',
                                    color: '#34a853'
                                }}>
                                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✓</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                                        All {validationResults.totalRecords} records passed validation!
                                    </div>
                                    <div style={{ color: '#5f6368', marginTop: '0.5rem' }}>
                                        All mandatory fields are present. You can proceed with uploading the Excel file.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div style={{ 
                            padding: '1rem 1.5rem', 
                            borderTop: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.5rem',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <McButton 
                                onClick={() => setShowValidationModal(false)} 
                                appearance="neutral"
                            >
                                Close
                            </McButton>
                            {validationResults.isValid && (
                                <McButton 
                                    onClick={() => {
                                        setShowValidationModal(false);
                                        handleExcelUpload();
                                    }} 
                                    appearance="primary"
                                >
                                    Proceed to Upload
                                </McButton>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Rejected FO Details Modal */}
            {showRejectedFOModal && rejectedFODetails && (
                <div style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 9999 
                }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        width: '90vw', 
                        maxWidth: '600px',
                        height: '500px', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', 
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Modal Header */}
                        <div style={{ 
                            padding: '1.5rem', 
                            borderBottom: '1px solid #e0e0e0',
                            flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#c5221f', fontWeight: 500 }}>
                                    {rejectedFODetails.missingFOs?.length > 0
                                        ? "Upload Rejected - FO Numbers Not Found"
                                        : "Upload Rejected"}
                                </h2>
                            </div>
                            {rejectedFODetails.fileName && (
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#5f6368' }}>
                                    File: {rejectedFODetails.fileName}
                                </p>
                            )}
                        </div>

                        {/* Summary Section */}
                        <div style={{
                            padding: '1rem 1.5rem',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e0e0e0',
                            flexShrink: 0
                        }}>
                            <p style={{ margin: 0, color: '#5f6368', fontSize: '0.9rem' }}>
                                {rejectedFODetails.missingFOs?.length > 0
                                    ? "The following FO numbers from your uploaded file were not found in the master data."
                                    : (rejectedFODetails.rawRemarks || "No additional details were provided for this rejection.")}
                            </p>
                        </div>

                        {/* Missing FOs List - Scrollable */}
                        {rejectedFODetails.missingFOs?.length > 0 && (
                            <div style={{
                                flex: 1,
                                overflow: 'hidden',
                                padding: '1.5rem',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0
                            }}>
                                <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: 600, color: '#333', flexShrink: 0 }}>
                                    Missing FO Numbers ({rejectedFODetails.missingFOs?.length || 0})
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.5rem',
                                    overflowY: 'auto',
                                    flex: 1,
                                    alignContent: 'flex-start'
                                }}>
                                    {rejectedFODetails.missingFOs?.map((fo, idx) => (
                                        <span key={idx} style={{
                                            backgroundColor: '#fce8e6',
                                            color: '#c5221f',
                                            padding: '0.2rem 0.5rem',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            height: 'fit-content'
                                        }}>
                                            {fo}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Instructions */}
                        <div style={{ 
                            padding: '0 1.5rem 1rem 1.5rem',
                            flexShrink: 0
                        }}>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#5f6368' }}>
                                Please verify that the FO numbers in your Excel file are correct and exist in the master data. Update the file and try uploading again.
                            </p>
                        </div>

                        {/* Modal Footer */}
                        <div style={{ 
                            padding: '1rem 1.5rem', 
                            borderTop: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.5rem',
                            backgroundColor: '#f8f9fa',
                            flexShrink: 0
                        }}>
                            <McButton 
                                onClick={() => {
                                    setShowRejectedFOModal(false);
                                    setRejectedFODetails(null);
                                }} 
                                appearance="neutral"
                            >
                                Close
                            </McButton>
                        </div>
                    </div>
                </div>
            )}

            <McToast appearance={toastAppearance} open={toastVisible} close={() => setToastVisible(false)}>
                <McNotification body={toastMessage} />
            </McToast>


            <McDialog
                heading="Missing ZIP Files"
                body="You haven’t uploaded ZIP folders. Do you want to proceed anyway?"
                open={dialogOpen}
                click={handleDialogClick}
                zindex={99999}
            >
                <McButton appearance="neutral" dialogaction="cancel" fit="medium">
                    Cancel
                </McButton>
                <McButton appearance="primary" dialogaction="ok" fit="medium">
                    Yes, proceed
                </McButton>
            </McDialog>


        </div>
    );
}

export default RequestCharge;